import { useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AutoCompleteDomains, AutoCompleteResult, Result, VersionedPeriod } from 'src/models';
import { getCustomIsLoading } from 'src/store/loading/loading.reducer';
import { useDebouncedFn } from './useDebouncedFn';
import { requestAutocomplete } from 'src/store/autocomplete/autocomplete.actions';

export function useAutocomplete(
  domain: AutoCompleteDomains,
  ids?: string[],
  invite_id?: string,
  period?: VersionedPeriod,
) {
  const [results, setResults] = useState<AutoCompleteResult[]>([]);

  const [isRequested, setIsRequested] = useState(false);

  const skipResponse = useRef(false);

  const dispatch = useDispatch();

  const isLoading = useSelector(getCustomIsLoading)(domain);

  function handleResults(response: Result) {
    if (skipResponse.current || !response.isOK) {
      return;
    }

    setIsRequested(true);
    setResults(response.payload as AutoCompleteResult[]);
  }

  const sendRequest = useCallback(
    (query: string) => {
      if (!skipResponse.current) {
        dispatch(
          requestAutocomplete({ domain, query, period, ids, invite_id }, { callback: handleResults, id: domain }),
        );
      }
    },
    [dispatch, domain, ids, invite_id, period],
  );

  const request = useDebouncedFn(sendRequest, 600);

  const onChangeHandler = useCallback(
    (value: string, minLength = 2) => {
      if (value.length >= minLength) {
        request(value);
        skipResponse.current = false;
      } else if (!value) {
        setIsRequested(false);
        setResults([]);
        skipResponse.current = true;
      }
    },
    [request],
  );

  return { results, isLoading, onChangeHandler, isRequested };
}
